/**
 * 🔴 换镜下标的纯函数（2026-10-14）。**唯一实现**，多处复用。
 *
 * 之前这些逻辑散在两个组件里各写一份（VideoStage 的弹层、底部信息面板），
 * 典型「两份实现」隐患：clamp 口径一旦漂移，就是「有的地方到头停、有的地方循环回第一镜」。
 * 这里下沉成纯模块，组件只负责渲染。
 */

/**
 * 换镜一步的下标（clamp 到清单范围内）。
 *
 * ⚠️ **不环形循环**：末镜点「下一镜」跳回首镜是另一种合法但不可预期的行为 ——
 * 用户不知道自己跳回去了，还以为从头开始。口径：到头禁用按钮 / 到尾停止播放。
 */
export function clampPlaylistIndex(index: number, total: number, delta: number): number {
  return Math.min(total - 1, Math.max(0, index + delta));
}

/** 某镜在可播清单里的下标；不在清单（没成片）或未选返回 -1 */
export function playlistIndexOf<T extends { shot: { id: string } }>(
  list: readonly T[],
  shotId: string | null
): number {
  if (!shotId) return -1;
  return list.findIndex((p) => p.shot.id === shotId);
}

/**
 * 二分：清单里第一条镜号 ≥ seq 的位置。
 *
 * 用于「当前镜没有成片」时确定跳转基准 —— 否则从 idx=-1 直接 +1 会跳回第一镜，
 * 表现为「点下一镜突然回到开头」。
 */
export function lowerBoundBySequence<T extends { shot: { sequence?: number | null } }>(
  list: readonly T[],
  seq: number
): number {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((list[mid].shot.sequence ?? 0) < seq) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}